/**
 * /spec-driven-development — SEO landing page targeting the
 * "spec driven development" / "spec-driven development ai" / "specification
 * driven development" / "spec first development" cluster. Audience: developers
 * and technical PMs building with AI coding agents.
 *
 * Differentiation angle: spec-driven development only works while the spec and
 * the code agree. The spec lives in a doc, the agent writes from a copy, a
 * decision changes — and spec, code, and the agent's context diverge. Draftlize
 * makes the spec an addressable card the agent reads over MCP before it writes,
 * and flips implementation status to drift_detected when the code no longer
 * matches. This is the developer face of Draftlize's stale propagation.
 *
 * English-only + inline (no i18n), mirroring the comparison / guide pages.
 * Chinese variant lives in SpecDrivenDevelopmentZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function SpecDrivenDevelopment() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/spec-driven-development"
      documentTitle="Spec-Driven Development for AI Agents · Draftlize"
      documentDescription="Spec-driven development means the spec drives the code — but the spec drifts from what the agent builds the moment work starts. Draftlize makes the spec a card Claude Code and Cursor read over MCP, and flags it drift_detected when the code diverges."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Spec-driven development</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Spec-driven development,<br/>
            <em>when the agent writes the code.</em>
          </h1>
          <p className="lede">
            Spec-driven development makes the specification the source of truth: you decide precisely what to build, and the implementation follows from the spec — increasingly written by an AI coding agent like Claude Code or Cursor. It works right up until the spec and the code diverge. The spec sits in a doc, the agent writes from a copy, a decision upstream changes, and now the spec, the code, and the agent&apos;s context all disagree — quietly, until something breaks. Draftlize makes the spec an addressable card the agent reads over MCP before it writes, and flips it to <em>drift_detected</em> the moment the implementation no longer matches.
          </p>
          <div style={{ marginTop: 32, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <a className="btn-primary" href="/">Start free with $5</a>
            <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>No card. Pay only for what you use.</span>
          </div>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">A spec in a doc vs <em>a spec the agent reads.</em></h2>
          </div>
          <p>Spec-driven development lives or dies on whether the spec still matches the code. A markdown spec cannot tell you when it stopped matching; a card that knows its dependencies can.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Spec in a doc / markdown</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>What the agent reads</td><td className="ours yes">Live cards over MCP</td><td className="no">A copy pasted into the prompt</td></tr>
            <tr><td>A decision changes</td><td className="ours yes">Dependent specs flag stale</td><td className="no">The spec silently goes wrong</td></tr>
            <tr><td>Implementation status</td><td className="ours yes">Flips to drift_detected on its own</td><td className="no">Whatever the doc said last</td></tr>
            <tr><td>One decision, six specs</td><td className="ours yes">One card, cited from all six</td><td className="no">Six copies that drift apart</td></tr>
            <tr><td>Who keeps spec and code aligned</td><td className="ours yes">The substrate tracks it</td><td className="no">Your memory</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why spec-driven development <em>breaks down.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">The spec and the code drift apart</h3>
            <p>You write the spec, the agent builds it, and both are correct on day one. Then the code evolves in pull requests the spec never hears about, and the spec evolves in edits the code never sees. Two sources of truth, diverging — which is the same as having none.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The agent writes from a stale copy</h3>
            <p>When you paste a <a href="/product-spec-template" style={{ color: 'var(--amber)' }}>spec</a> into a prompt, the agent builds from a snapshot frozen at copy time. Change a decision after that and the agent keeps implementing the old one — confidently, because nothing told it the ground moved.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">One decision changes, six specs lie</h3>
            <p>Flip a decision the API contract rested on and every spec that assumed it is now wrong. Nothing links the decision to the specs, so they read as truth until an engineer — or the agent — trips over the contradiction in review.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Spec-driven development on <em>a live substrate.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">The spec is a card the agent reads first</h3>
            <p>Claude Code or Cursor pulls the relevant spec and the decisions under it over MCP before it writes a line — so it implements the current definition, not a paste from last week. The spec is context the agent reads on every turn, not a document it saw once.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change a decision, dependent specs flag stale</h3>
            <p>Each spec card declares what it depends on. Change an upstream <a href="/decision-log" style={{ color: 'var(--amber)' }}>decision</a> and every spec built on it turns stale automatically — the way a build system invalidates everything downstream of a changed file.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Implementation flips to drift_detected</h3>
            <p>When the spec moves after the code was written, the implementation status flips itself to <em>drift_detected</em> — so the gap between spec and code surfaces the moment it opens, not three sprints later in a post-mortem.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Spec-driven development is only as strong as the link between the spec and the code. Make that link something the substrate maintains, not something you remember to.
        </blockquote>
        <cite>Write the spec once. Let the agent read the live version, every turn.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is spec-driven development?', a: 'Spec-driven development is an approach where a precise specification is the source of truth and the implementation follows from it, rather than the design emerging from the code as you write. In the AI era the spec is often what a human writes and the code is what an agent generates from it, which makes keeping the two in sync the central problem.' },
            { q: 'How is spec-driven development different from test-driven development?', a: 'Test-driven development writes a failing test first, then code to pass it. Spec-driven development writes the specification first, then implements to satisfy it — and tests can be one expression of that spec. The two are complementary: the spec says what to build and why, the tests check that the build matches.' },
            { q: 'Does spec-driven development work with AI coding agents?', a: 'It is arguably where it matters most. An agent like Claude Code or Cursor implements from whatever spec it is given, so the freshness of the spec directly determines the code. The failure mode is the agent building from a stale copy — which is exactly what an addressable, agent-read spec avoids.' },
            { q: 'How do you keep the spec and the code in sync?', a: 'Stop storing the spec as a static copy. Draftlize makes the spec a card the agent reads over MCP before it writes, wires each spec to the decisions it depends on, and flips implementation status to drift_detected when an upstream decision changes — so divergence surfaces the moment it happens instead of in review.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Give your agent a spec that <em>stays true — free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Write specs as cards, connect Claude Code or Cursor over MCP, and let the implementation flag drift the moment a decision moves. See <a href="/for-developers" style={{ color: 'var(--amber)' }}>Draftlize for developers</a>.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
